import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Save, X } from 'lucide-react';
import axios from 'axios';

const TaskDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [task, setTask] = useState(null);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [editedTask, setEditedTask] = useState({});

    const fetchTask = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            console.log('Fetching task with ID:', id);
            const response = await axios.get(`/tasks/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('Task data received:', response.data);
            setTask(response.data);
            setEditedTask(response.data);
        } catch (error) {
            console.error('Error fetching task:', error);
            alert(`Error fetching task: ${error.response?.data?.message || error.message}`);
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchLeads = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/leads', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLeads(response.data);
        } catch (error) {
            console.error('Error fetching leads:', error);
        }
    };

    useEffect(() => {
        fetchTask();
        fetchLeads();
    }, [fetchTask]);

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('token');
            console.log('Saving task:', editedTask);
            const response = await axios.put(`/tasks/${id}`, editedTask, {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('Task updated successfully:', response.data);
            setTask(response.data);
            setEditing(false);
            alert('Task updated successfully!');
        } catch (error) {
            console.error('Error updating task:', error);
            alert(`Error updating task: ${error.response?.data?.message || error.message}`);
        }
    };

    const handleCancel = () => {
        setEditedTask(task);
        setEditing(false);
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'bg-red-100 text-red-800';
            case 'medium': return 'bg-yellow-100 text-yellow-800';
            case 'low': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'completed': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!task) {
        return (
            <div className="p-6">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-900">Task not found</h2>
                    <button
                        onClick={() => navigate('/tasks')}
                        className="mt-4 text-blue-600 hover:text-blue-800"
                    >
                        Back to Tasks
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/tasks')}
                        className="text-gray-600 hover:text-gray-900"
                    >
                        <ArrowLeft size={24} />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900">Task Details</h1>
                </div>
                <div className="flex gap-2">
                    {editing ? (
                        <>
                            <button
                                onClick={handleSave}
                                className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
                            >
                                <Save size={16} />
                                Save
                            </button>
                            <button
                                onClick={handleCancel}
                                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-400"
                            >
                                <X size={16} />
                                Cancel
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={() => setEditing(true)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
                        >
                            <Edit size={16} />
                            Edit
                        </button>
                    )}
                </div>
            </div>

            {/* Task Information */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Basic Information */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Title</label>
                                {editing ? (
                                    <input
                                        type="text"
                                        value={editedTask.title}
                                        onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
                                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900">{task.title}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                {editing ? (
                                    <textarea
                                        value={editedTask.description || ''}
                                        onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
                                        rows={3}
                                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900">{task.description || 'No description'}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Assigned To</label>
                                {editing ? (
                                    <input
                                        type="text"
                                        value={editedTask.assigned_to || ''}
                                        onChange={(e) => setEditedTask({ ...editedTask, assigned_to: e.target.value })}
                                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900">{task.assigned_to || '-'}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Task Details */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Task Details</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Status</label>
                                {editing ? (
                                    <select
                                        value={editedTask.status}
                                        onChange={(e) => setEditedTask({ ...editedTask, status: e.target.value })}
                                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="pending">Pending</option>
                                        <option value="completed">Completed</option>
                                    </select>
                                ) : (
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(task.status)}`}>
                                        {task.status}
                                    </span>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Priority</label>
                                {editing ? (
                                    <select
                                        value={editedTask.priority}
                                        onChange={(e) => setEditedTask({ ...editedTask, priority: e.target.value })}
                                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                    </select>
                                ) : (
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(task.priority)}`}>
                                        {task.priority}
                                    </span>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Due Date</label>
                                {editing ? (
                                    <input
                                        type="date"
                                        value={editedTask.due_date || ''}
                                        onChange={(e) => setEditedTask({ ...editedTask, due_date: e.target.value })}
                                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900">
                                        {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Related Lead</label>
                                {editing ? (
                                    <select
                                        value={editedTask.lead_id || ''}
                                        onChange={(e) => setEditedTask({ ...editedTask, lead_id: e.target.value || null })}
                                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">No lead</option>
                                        {leads.map(lead => (
                                            <option key={lead.id} value={lead.id}>{lead.name}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900">
                                        {task.lead_id ? leads.find(l => l.id === task.lead_id)?.name || 'Unknown lead' : 'No lead'}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Created</label>
                                <p className="mt-1 text-sm text-gray-900">
                                    {new Date(task.created_at).toLocaleDateString()} at {new Date(task.created_at).toLocaleTimeString()}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskDetail; 